import { useEffect, useState } from "react";
import { Alert, Empty, Modal, Working } from "../ui";
import type { Catalog, StaffMember } from "./engine";

/**
 * Who is at the till, and who allows a void or a correction: tap a name, enter that
 * person's password.
 *
 * Used two ways. To switch the seller, so every sale shows who made it. And to allow a void
 * or a correction, where only people allowed to void are listed. There used to be a
 * four-digit PIN for both, set once with the password; D asked for it to go (4 October
 * 2026: "double entry, very troublesome; remove all the PINs"), so it is the password each
 * time. The server checks it, with the same wrong-password count and lock as signing in,
 * so this needs a connection. Switching to someone is not free either: a manager's name
 * would otherwise carry a manager's rights to whoever tapped it.
 */
export function PosStaff({
  catalog,
  title,
  currentId,
  onlyVoiders = false,
  onChosen,
  onClose,
}: {
  readonly catalog: Catalog;
  readonly title: string;
  readonly currentId?: string | undefined;
  readonly onlyVoiders?: boolean;
  readonly onChosen: (member: StaffMember) => void;
  readonly onClose: () => void;
}): React.ReactElement {
  const [members, setMembers] = useState(catalog.staff);
  const [chosen, setChosen] = useState<StaffMember | undefined>();
  const shown = members.filter((member) => !onlyVoiders || member.canVoid);

  useEffect(() => setMembers(catalog.staff), [catalog]);

  return (
    <Modal open overPanel title={chosen ? chosen.name : title} onClose={onClose}>
      {!chosen ? (
        shown.length === 0 ? (
          <Empty icon="ri-user-line">Nobody on this iPad can do this yet.</Empty>
        ) : (
          <div className="grid grid-cols-3 gap-3">
            {shown.map((member) => (
              <button
                key={member.id}
                type="button"
                className={`ti-btn ${member.id === currentId ? "ti-btn-primary" : "ti-btn-light"} !m-0 !flex !flex-col !items-center !gap-2 !py-4 !h-auto`}
                onClick={() => {
                  // Whoever is selling already needs nothing more to carry on.
                  if (member.id === currentId && !onlyVoiders) onChosen(member);
                  else setChosen(member);
                }}
              >
                <span className="avatar avatar-md avatar-rounded !bg-primary/10 !text-primary font-semibold !mb-0">
                  {initials(member.name)}
                </span>
                <span className="text-[0.813rem] font-semibold leading-tight text-center">{member.name}</span>
                <span className="text-[0.6875rem] text-[#8c9097] dark:text-white/50">
                  {member.id === currentId && !onlyVoiders ? "Current salesperson" : "Password"}
                </span>
              </button>
            ))}
          </div>
        )
      ) : (
        <WithPassword
          member={chosen}
          purpose={onlyVoiders ? "allow" : "sell"}
          onBack={() => setChosen(undefined)}
          onRight={() => onChosen(chosen)}
        />
      )}
    </Modal>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((word) => word.charAt(0))
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

/** The person's own password, checked by the server. Needs a connection. */
function WithPassword({
  member,
  purpose,
  onBack,
  onRight,
}: {
  readonly member: StaffMember;
  /** To allow a void or a correction, or to start selling as this person. */
  readonly purpose: "allow" | "sell";
  readonly onBack: () => void;
  readonly onRight: () => void;
}): React.ReactElement {
  const [password, setPassword] = useState("");
  const [problem, setProblem] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  async function check(): Promise<void> {
    setBusy(true);
    setProblem(undefined);
    try {
      const response = await fetch("/api/pos/approve", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: member.id, password, purpose }),
      });
      const body = (await response.json().catch(() => ({}))) as { error?: string };
      if (!response.ok) {
        setProblem(response.status === 429 ? "Too many attempts. Wait a minute and try again" : (body.error ?? "That did not work"));
        return;
      }
      onRight();
    } catch {
      setProblem("Password approval requires an internet connection. Try again when the iPad is online");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (password && !busy) void check();
      }}
    >
      <p className="text-[#8c9097] dark:text-white/50">
        {purpose === "allow"
          ? `${member.name}, enter your password to approve.`
          : `${member.name}, enter your password to start selling.`}
      </p>
      {problem ? <Alert tone="danger">{problem}</Alert> : null}
      <label htmlFor="staff-password" className="form-label">
        Password
      </label>
      <input
        id="staff-password"
        type="password"
        autoComplete="off"
        className="form-control"
        value={password}
        autoFocus
        onChange={(event) => setPassword(event.target.value)}
      />
      <div className="flex justify-between gap-2 mt-4">
        <button type="button" className="ti-btn ti-btn-light !mb-0" onClick={onBack}>
          <i className="ri-arrow-left-line" /> Someone else
        </button>
        <button type="submit" className="ti-btn ti-btn-primary-full !mb-0" disabled={busy || !password}>
          {busy ? <Working label="Checking" /> : purpose === "allow" ? "Approve" : "Start selling"}
        </button>
      </div>
    </form>
  );
}
